/**
 * Adapterkontrakt. MockAdapter, CsvAdapter og ApiAdapter implementerer samme grensesnitt;
 * skjermene bruker bare dette grensesnittet og trenger ikke omskriving ved bytte.
 */
import type {
  ActivityAllocation,
  Dataset,
  FieldCoverage,
  LineStateInterval,
  MachineInterval,
  ProductionInterval,
  SourceSync,
} from '../domain/types';

export interface AdapterQuery {
  siteId: string;
  fromUtc: string;
  toUtc: string;
  scenarioId: string;
}

export interface SourceRef {
  sourceId: string;
  recordIds: string[];
}

export interface AdapterWarning {
  code: string;
  message: string;
}

export interface AdapterResponse<T> {
  data: T[];
  coverage: FieldCoverage[];
  warnings: AdapterWarning[];
  sourceRefs: SourceRef[];
}

export type AdapterKind = 'mock' | 'csv' | 'api';

export interface DataAdapter {
  readonly kind: AdapterKind;
  readonly label: string;
  readonly isSimulated: true;
  getProduction(query: AdapterQuery): Promise<AdapterResponse<ProductionInterval>>;
  getLineStates(query: AdapterQuery): Promise<AdapterResponse<LineStateInterval>>;
  getMachineIntervals(query: AdapterQuery): Promise<AdapterResponse<MachineInterval>>;
  getAllocations(query: AdapterQuery): Promise<AdapterResponse<ActivityAllocation>>;
  getCoverage(query: AdapterQuery): Promise<FieldCoverage[]>;
  getSourceStatus(scenarioId: string): Promise<SourceSync[]>;
  /** Henter hele det normaliserte datasettet for demovinduet (brukes av beregningsmotoren). */
  getDataset(scenarioId: string): Promise<Dataset>;
}

export function filterByRange<T extends { fromUtc: string }>(items: T[], q: AdapterQuery): T[] {
  return items.filter((x) => x.fromUtc >= q.fromUtc && x.fromUtc < q.toUtc);
}

export function coverageFor(intervals: MachineInterval[], production: ProductionInterval[]): FieldCoverage[] {
  const n = intervals.length;
  const count = (f: (m: MachineInterval) => boolean) => intervals.filter(f).length;
  const mk = (field: string, unit: string, received: number, expected: number): FieldCoverage => ({
    field,
    unit,
    expected,
    received,
    status: expected === 0 ? 'not_applicable' : received === expected ? 'complete' : received === 0 ? 'missing' : 'partial',
    sourceId: field === 'producedTonnes' ? 'produksjon' : 'maskindata',
    countsLabel: field === 'producedTonnes' ? 'produksjonsposter' : 'maskinintervaller',
  });
  return [
    mk('producedTonnes', 't', production.filter((p) => p.producedTonnes !== null).length, production.length),
    mk('engineHours', 'h', count((m) => m.engineHours !== null), n),
    mk('idleHours', 'h', count((m) => m.idleHours !== null), n),
    mk('fuelLitres', 'L', count((m) => m.fuelLitres !== null), n),
  ];
}
